'use client';

import { useState, useEffect, useCallback } from 'react';
import { usePathname, useParams, useSearchParams } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/lib/components/ui/dialog';
import { Button } from '@/lib/components/ui/button';
import { Textarea } from '@/lib/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/lib/components/ui/select';
import { Label } from '@/lib/components/ui/label';

const ISSUE_TYPES = [
  { value: 'DATA_ISSUE', label: 'Data Issue' },
  { value: 'UI_ISSUE', label: 'UI Issue' },
  { value: 'GENERAL_NOTE', label: 'General Note' },
] as const;

type IssueType = (typeof ISSUE_TYPES)[number]['value'];

interface SystemDetails {
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  browser: string;
  browserVersion: string;
  os: string;
  platform: string;
  language: string;
  cookiesEnabled: boolean;
  onLine: boolean;
  timezone: string;
}

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function getBrowserInfo(): { browser: string; version: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = '';

  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    version = ua.split('Firefox/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
    version = ua.split('Edg/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    version = ua.split('Chrome/')[1]?.split(' ')[0] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
    version = ua.split('Version/')[1]?.split(' ')[0] || '';
  }

  return { browser, version };
}

function getOSInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows NT 10')) return 'Windows 10/11';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS X')) {
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    return match ? `macOS ${match[1].replace('_', '.')}` : 'macOS';
  }
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

function getSystemDetails(): SystemDetails {
  const browserInfo = getBrowserInfo();
  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    browser: browserInfo.browser,
    browserVersion: browserInfo.version,
    os: getOSInfo(),
    platform: navigator.platform,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

async function captureScreenshot(): Promise<string | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const html2canvas = (await import('html2canvas' as any)).default as (
      element: HTMLElement,
      options?: Record<string, unknown>
    ) => Promise<HTMLCanvasElement>;
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      allowTaint: true,
      scale: 0.5, // Reduce size for storage
      ignoreElements: (element: Element) => {
        // Ignore dialogs, modals, overlays, toasts, and other floating elements
        if (element.closest('[role="dialog"]')) return true;
        if (element.closest('[data-radix-popper-content-wrapper]')) return true;
        if (element.closest('[data-state="open"]')) return true;
        if (element.closest('.fixed')) return true;
        if (element.closest('[role="alertdialog"]')) return true;
        if (element.closest('[data-sonner-toaster]')) return true;
        // Check for common overlay/backdrop classes
        const className = element.className?.toString() || '';
        if (className.includes('overlay') || className.includes('backdrop') || className.includes('modal')) {
          return true;
        }
        // Check for elements with very high z-index (likely overlays)
        const computedStyle = window.getComputedStyle(element);
        const zIndex = parseInt(computedStyle.zIndex, 10);
        if (!isNaN(zIndex) && zIndex > 1000) return true;
        return false;
      },
    });
    return canvas.toDataURL('image/jpeg', 0.6); // JPEG with 60% quality for smaller size
  } catch {
    // Silently fail - screenshot is optional and should not block issue reporting
    return null;
  }
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [message, setMessage] = useState('');
  const [issueType, setIssueType] = useState<IssueType>('DATA_ISSUE');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();

  // Get full URL with query parameters
  const getFullUrl = () => {
    const queryString = searchParams.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  };

  // Capture screenshot when modal opens
  const captureOnOpen = useCallback(async () => {
    if (isOpen && !screenshot) {
      setIsCapturingScreenshot(true);
      const captured = await captureScreenshot();
      setScreenshot(captured);
      setIsCapturingScreenshot(false);
    }
  }, [isOpen, screenshot]);

  useEffect(() => {
    captureOnOpen();
  }, [captureOnOpen]);

  // Extract entity info from URL path
  const getEntityInfo = () => {
    let entity = 'PAGE';
    let entityId: string | null = null;

    // Detect entity type from path
    if (pathname.includes('/scenarios/')) {
      entity = 'SCENARIO';
      entityId = (params.id as string) || null;
    } else if (pathname.includes('/plans/')) {
      entity = 'PLAN';
      entityId = (params.id as string) || null;
    } else if (pathname.includes('/walkforward')) {
      entity = 'WALKFORWARD';
      entityId = (params.id as string) || null;
    } else if (pathname.includes('/administration/listprice')) {
      entity = 'LISTPRICE';
    } else if (pathname.includes('/administration/volume')) {
      entity = 'VOLUME';
    } else if (pathname.includes('/export')) {
      entity = 'EXPORT';
    }

    return { entity, entityId };
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please describe the issue');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { entity, entityId } = getEntityInfo();
      const systemDetails = getSystemDetails();

      const response = await fetch('/api/app-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          entity,
          entity_id: entityId,
          reported_by: 'USER',
          operation: 'VIEW',
          status: issueType === 'GENERAL_NOTE' ? 'INFO' : 'ERROR',
          issue_type: issueType,
          message: message.trim(),
          metadata: {
            page_url: getFullUrl(),
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            system: systemDetails,
            ...(screenshot ? { screenshot } : {}),
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setSuccess(true);
      setMessage('');

      // Close modal after brief success message
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (_err) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setIssueType('DATA_ISSUE');
    setScreenshot(null);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const { entity, entityId } = getEntityInfo();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='w-[50%] max-w-[50%]'>
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
          <DialogDescription>Describe the issue you are experiencing</DialogDescription>
        </DialogHeader>

        {success ? (
          <div className='py-6 text-center'>
            <p className='text-green-600 font-medium'>Thank you for your feedback!</p>
            <p className='text-sm text-muted-foreground mt-1'>Your report has been submitted.</p>
          </div>
        ) : (
          <>
            <div className='grid gap-4 py-4'>
              <div className='space-y-2'>
                <Label htmlFor='issue-type'>Issue Type</Label>
                <Select value={issueType} onValueChange={(value: IssueType) => setIssueType(value)}>
                  <SelectTrigger id='issue-type'>
                    <SelectValue placeholder='Select issue type' />
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='description'>Description</Label>
                <Textarea
                  id='description'
                  placeholder="What's happening?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className='resize-none text-base'
                />
                {error && <p className='text-sm text-red-500 mt-1'>{error}</p>}
              </div>

              <div className='text-xs text-muted-foreground space-y-1'>
                <p className='break-all'>
                  <span className='font-medium'>URL:</span> {getFullUrl()}
                </p>
                <p>
                  <span className='font-medium'>Page:</span> {entity}
                  {entityId ? ` (${entityId.length > 16 ? `${entityId.substring(0, 16)}...` : entityId})` : ''}
                </p>
                {screenshot && !isCapturingScreenshot && <p className='text-green-600'>Screenshot captured</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
                Submit
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
